/**
 * Jaipur Handloom navigation controller — 18AE22
 *
 * Sole owner of:
 * - Desktop mega-menu open/close and collection-panel switching.
 * - Mobile navigation open/close, submenu disclosure and keyboard focus.
 * - Preview-safe legacy home/support links.
 *
 * Do not bind menu toggles in another file. Search visibility remains owned by
 * jh-50-responsive-ui.js because it is independent from navigation.
 */
(function() {
  'use strict';

  var JH = window.JH || {};
  var Utils = JH.Utils;
  if (!Utils) return;

  var navigation = null;
  var mobileToggle = null;

  function isDesktop() {
    return !!(window.matchMedia && window.matchMedia('(min-width: 901px)').matches);
  }

  function activeThemeRole() {
    var role = '';
    try {
      role = window.JHConfig && window.JHConfig.theme && window.JHConfig.theme.role;
      if (!role && window.Shopify && Shopify.theme) role = Shopify.theme.role;
    } catch (error) {}
    return String(role || '').toLowerCase();
  }

  function preservePreview(url) {
    try {
      var target = new URL(url, window.location.origin);
      var current = new URL(window.location.href);
      var role = activeThemeRole();
      var isPreviewTheme = role === 'unpublished' || role === 'development' || role === 'demo';

      // Preview parameters are Shopify-internal. Preserve them only when the
      // current theme explicitly reports a preview-only role. Unknown roles are
      // treated as live so production links never inherit preview parameters.
      ['preview_theme_id', '_fd', 'pb', 'section_id'].forEach(function(key) {
        target.searchParams.delete(key);
      });
      if (isPreviewTheme) {
        ['preview_theme_id', '_fd', 'pb'].forEach(function(key) {
          if (current.searchParams.has(key)) target.searchParams.set(key, current.searchParams.get(key));
        });
      }
      return target.pathname + target.search + target.hash;
    } catch (error) {
      return url;
    }
  }

  function fixLegacyLinks(scope) {
    Utils.qsa('a[data-jh-home-link]', scope || document).forEach(function(link) {
      link.setAttribute('href', preservePreview(Utils.rootUrl()));
    });
  }

  function itemToggle(item) {
    return Utils.qs('[data-jh-auto-mm-toggle]', item);
  }

  function closeItem(item) {
    if (!item) return;
    item.classList.remove('is-open');
    var toggle = itemToggle(item);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function closeMegaMenus(exceptItem) {
    Utils.qsa('[data-jh-auto-mm-item].is-open', navigation || document).forEach(function(item) {
      if (item !== exceptItem) closeItem(item);
    });
  }

  function openItem(item) {
    if (!item) return;
    closeMegaMenus(item);
    item.classList.add('is-open');
    var toggle = itemToggle(item);
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  function activateTile(tile) {
    if (!tile) return;
    var content = tile.closest('.jh-auto-mm-content');
    if (!content) return;
    var targetId = tile.getAttribute('data-jh-auto-mm-target');

    Utils.qsa('[data-jh-auto-mm-tile]', content).forEach(function(item) {
      item.classList.toggle('is-active', item === tile);
    });
    Utils.qsa('[data-jh-auto-mm-panel]', content).forEach(function(panel) {
      panel.classList.toggle('is-active', !!targetId && panel.id === targetId);
    });
  }

  function setMobileNavTop() {
    var header = Utils.qs('[data-jh-header]') || Utils.qs('.jh-header');
    var top = header ? Math.max(54, Math.round(header.getBoundingClientRect().bottom)) : 88;
    document.documentElement.style.setProperty('--jh-mobile-nav-top', top + 'px');
  }

  function syncResponsiveAccessibility() {
    if (!navigation) return;
    if (isDesktop()) {
      navigation.removeAttribute('aria-hidden');
      navigation.classList.remove('is-open');
      document.documentElement.classList.remove('jh-mobile-nav-open');
      if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
      return;
    }
    navigation.setAttribute('aria-hidden', navigation.classList.contains('is-open') ? 'false' : 'true');
  }

  function openMobileNav() {
    if (!navigation || !mobileToggle || isDesktop()) return;
    // Opening mobile menu must dismiss search rather than stack two overlays.
    var searchRoot = Utils.qs('[data-jh-search-root]');
    if (searchRoot) {
      var searchPanel = Utils.qs('[data-jh-search-panel]', searchRoot);
      var searchInput = Utils.qs('[data-jh-search-input]', searchRoot);
      searchRoot.dataset.jhSearchDismissed = 'true';
      searchRoot.classList.remove('jh-search-has-query');
      if (searchPanel) { searchPanel.classList.remove('is-open'); searchPanel.setAttribute('aria-hidden', 'true'); }
      if (searchInput) searchInput.blur();
    }
    setMobileNavTop();
    navigation.classList.add('is-open');
    navigation.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('jh-mobile-nav-open');
    mobileToggle.setAttribute('aria-expanded', 'true');
    navigation.__jhReturnFocus = mobileToggle;

    var first = Utils.qs('a[href],button:not([disabled])', navigation);
    if (first) window.setTimeout(function() { first.focus(); }, 0);
  }

  function closeMobileNav(returnFocus) {
    if (!navigation || !mobileToggle) return;
    navigation.classList.remove('is-open');
    if (!isDesktop()) navigation.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('jh-mobile-nav-open');
    mobileToggle.setAttribute('aria-expanded', 'false');
    closeMegaMenus(null);

    if (returnFocus !== false && navigation.__jhReturnFocus && document.contains(navigation.__jhReturnFocus)) {
      var target = navigation.__jhReturnFocus;
      window.setTimeout(function() {
        // Do not steal focus if a visitor immediately tapped the mobile search field.
        if (document.activeElement === document.body || document.activeElement === target) target.focus();
      }, 0);
    }
    navigation.__jhReturnFocus = null;
  }

  function bindMegaMenuItem(item) {
    if (item.dataset.jhMegaReady === 'true') return;
    item.dataset.jhMegaReady = 'true';

    var toggle = itemToggle(item);
    var firstTile = Utils.qs('[data-jh-auto-mm-tile]', item);
    if (firstTile) activateTile(firstTile);

    if (toggle) {
      toggle.addEventListener('click', function(event) {
        if (!isDesktop()) return;
        event.preventDefault();
        event.stopPropagation();
        if (item.classList.contains('is-open')) closeItem(item);
        else openItem(item);
      });

      toggle.addEventListener('keydown', function(event) {
        if (!isDesktop() || (event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        openItem(item);
        var firstLink = Utils.qs('.jh-auto-mm-panel a[href], .jh-auto-mm-tiles a[href]', item);
        if (firstLink) firstLink.focus();
      });
    }

    item.addEventListener('mouseenter', function() {
      if (isDesktop()) openItem(item);
    });
    item.addEventListener('mouseleave', function() {
      if (isDesktop()) closeItem(item);
    });

    Utils.qsa('[data-jh-auto-mm-tile]', item).forEach(function(tile) {
      ['mouseenter', 'focus'].forEach(function(eventName) {
        tile.addEventListener(eventName, function() { activateTile(tile); });
      });
      tile.addEventListener('touchstart', function() { activateTile(tile); }, { passive: true });
    });
  }

  // QA36: after another open panel collapses, reposition the tapped disclosure
  // at the beginning of the independent mobile navigation scroller.
  // This avoids Chromium scroll-anchoring to the former panel's last links.
  function alignMobileDisclosure(item) {
    if (!navigation || !item || isDesktop()) return;
    window.requestAnimationFrame(function() {
      var title = item.querySelector('.jh-qa21-nav__link') || item;
      var navTop = navigation.getBoundingClientRect().top;
      var titleTop = title.getBoundingClientRect().top;
      var nextTop = Math.max(0, navigation.scrollTop + titleTop - navTop - 8);
      navigation.scrollTop = nextTop;
    });
  }

  function bindMobileNavigation() {
    if (!navigation || !mobileToggle || mobileToggle.dataset.jhMobileNavReady === 'true') return;
    mobileToggle.dataset.jhMobileNavReady = 'true';

    mobileToggle.addEventListener('click', function(event) {
      if (isDesktop()) return;
      event.preventDefault();
      event.stopPropagation();
      if (navigation.classList.contains('is-open')) closeMobileNav();
      else openMobileNav();
    });

    navigation.addEventListener('click', function(event) {
      if (isDesktop()) return;

      var toggle = event.target.closest && event.target.closest('[data-jh-auto-mm-toggle]');
      if (toggle && navigation.contains(toggle)) {
        event.preventDefault();
        event.stopPropagation();
        var item = toggle.closest('[data-jh-auto-mm-item], .jh-nav__item');
        if (!item) return;
        if (item.classList.contains('is-open')) closeItem(item);
        else openItem(item);
        alignMobileDisclosure(item);
        return;
      }

      var link = event.target.closest && event.target.closest('a[href]');
      if (!link || !navigation.contains(link)) return;
      var href = link.getAttribute('href') || '';
      if (!href || href === '#' || href.indexOf('javascript:') === 0) return;

      var parentItem = link.closest('[data-jh-auto-mm-item], .jh-nav__item');
      var isTopLink = parentItem && link.parentElement === parentItem && (link.classList.contains('jh-auto-nav__link') || link.classList.contains('jh-nav__link') || link.classList.contains('jh-qa21-nav__link'));
      var hasPanel = parentItem && Utils.qs('.jh-auto-mm-panel, .jh-auto-mm-tiles, [data-jh-mega-panel]', parentItem);

      if (isTopLink && hasPanel && !parentItem.classList.contains('is-open')) {
        event.preventDefault();
        openItem(parentItem);
        alignMobileDisclosure(parentItem);
        return;
      }

      closeMobileNav(false);
    }, true);
  }

  function bindDocumentControls() {
    if (document.documentElement.dataset.jhNavigationDocumentReady === 'true') return;
    document.documentElement.dataset.jhNavigationDocumentReady = 'true';

    document.addEventListener('click', function(event) {
      if (isDesktop()) {
        if (!event.target.closest('[data-jh-auto-nav]')) closeMegaMenus(null);
      } else if (navigation && navigation.classList.contains('is-open')) {
        var header = Utils.qs('[data-jh-header]') || Utils.qs('.jh-header');
        if (!header || !header.contains(event.target)) closeMobileNav();
      }
    });

    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape') {
        if (navigation && navigation.classList.contains('is-open') && !isDesktop()) closeMobileNav();
        else closeMegaMenus(null);
      } else if (event.key === 'Tab' && navigation && navigation.classList.contains('is-open') && !isDesktop()) {
        JH.A11y.trapTab(event, navigation);
      }
    });

    window.addEventListener('resize', Utils.debounce(function() {
      setMobileNavTop();
      syncResponsiveAccessibility();
    }, 120), { passive: true });
  }

  function init(scope) {
    fixLegacyLinks(scope || document);
    navigation = Utils.qs('#JHMainNav') || Utils.qs('[data-jh-nav]');
    mobileToggle = Utils.qs('[data-jh-mobile-menu-toggle]');
    if (!navigation) return;

    Utils.qsa('[data-jh-auto-mm-item]', navigation).forEach(bindMegaMenuItem);
    bindMobileNavigation();
    bindDocumentControls();
    setMobileNavTop();
    syncResponsiveAccessibility();
  }

  Utils.ready(function() { init(document); });
  document.addEventListener('shopify:section:load', function(event) {
    init(event.target);
  });
})();
